"use client";

import Link from "next/link";
import Script from "next/script";
import React, { useState } from "react";

interface Grecaptcha {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare let grecaptcha: Grecaptcha;

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (typeof grecaptcha === "undefined") {
      console.error("reCAPTCHA client is not available.");
      setErrorMessage("reCAPTCHA client is not ready. Please try again later.");
      setLoading(false);
      return;
    }

    grecaptcha.ready(async () => {
      try {
        const token = await grecaptcha.execute(SITE_KEY, {
          action: "submit",
        });

        const response = await fetch("/api/send-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...formData, token, formType: "contact" }),
        });

        if (response.ok) {
          const responseData = await response.json();
          setSuccessMessage(responseData.message);
          setSubmitted(true);
          setErrorMessage("");
        } else {
          const errorData = await response.json();
          setErrorMessage(errorData.message || "Error submitting form.");
          setSuccessMessage("");
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        setErrorMessage("An error occurred. Please try again.");
        setSuccessMessage("");
      } finally {
        setLoading(false);
      }
    });
  };

  const handleReset = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      city: "",
    });
    setSubmitted(false);
    setSuccessMessage("");
    setErrorMessage("");
  };

  return (
    <section className="w-full">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`}
        strategy="lazyOnload"
      />

      <div className="">
        <div className="">
          {submitted ? (
            <div className="text-center">
              {successMessage && (
                <p className="text-xl text-green-500">{successMessage}</p>
              )}
              {errorMessage && (
                <p className="text-xl text-red-500">{errorMessage}</p>
              )}
              <button
                onClick={handleReset}
                className="mt-4 rounded bg-purple-950 px-4 py-2 font-bold text-white hover:bg-rose-400"
              >
                Reset Form
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="w-full rounded-xl bg-gradient-to-br from-orange-400 to-pink-500 p-6 shadow-lg">
                <h2 className="mb-6 text-center text-lg font-bold uppercase tracking-wider text-white">
                  Book an Appointment for Expert Guidance
                </h2>
                <div className="mb-4 flex flex-col gap-4">
                  <div>
                    <label className="mb-2 block font-bold text-white">
                      Name:
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-md border p-2"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-bold text-white">
                      Email: (optional)
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full rounded-md border p-2"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-bold text-white">
                      Phone:
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full rounded-md border p-2"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block font-bold text-white">
                      City:
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full rounded-md border p-2"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full rounded-lg px-4 py-3 font-bold text-white transition-colors ${
                    loading
                      ? "cursor-not-allowed bg-gray-500"
                      : "bg-purple-950 hover:bg-rose-400"
                  }`}
                >
                  {loading ? (
                    <svg
                      className="mx-auto size-5 animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      ></path>
                    </svg>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
              <p className="py-4 text-xs">
                By submitting this form, I agree to let Trichos contact me via
                phone, text, email, or mail about appointments, services, or
                offers.This site is protected by reCAPTCHA and the Google.
                <Link
                  className="px-2 text-blue-600"
                  href="https://policies.google.com/privacy"
                >
                  Privacy Policy
                </Link>
                and
                <Link
                  className="px-2 text-blue-600"
                  href="https://policies.google.com/terms"
                >
                  Terms of Service
                </Link>
                apply.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
